import React, { useState } from 'react';
import { Box, Button, Fade, IconButton, Modal, Paper, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { API, graphqlOperation } from 'aws-amplify';

import { freezeFinancialYear, transferAccountsToFinancialYear, unFreezeFinancialYear } from '../../graphql';
import { fetchUserPreference } from '../../redux/reducers/UserPreference';
import { showErrorSnackbar, showSuccessSnackbar } from '../snackBar/snakbar';
import { LoadingButton } from '@mui/lab';
import { CreateFinancialYearAndTransferData } from '../../features/createFinancialYear/api/createFinancialyear.api';
import { formatUnixTimestamp } from '../../library';

const transferContent =
  "Hitting 'Sync Now' will transfer all active and inactive groups, ledgers, items, and closing balances from the previous year. " +
  "This will update your opening balances to match last year's closing figures.";

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '85%', sm: '70%', md: '50%' },
  maxHeight: '80vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  borderRadius: 2,
  p: 0,
  overflow: 'hidden',
};

const formatRange = (dateObj = {}) => {
  if (!dateObj) return 'Invalid date';
  const { startdate, enddate } = dateObj;
  return startdate && enddate
    ? `${moment(startdate * 1000).format('DD MMMM YYYY')} – ${moment(enddate * 1000).format('DD MMMM YYYY')}`
    : 'Invalid date';
};

export default function YearEndClosingAndFreezing({ open, item, setOpenModal, nextFYPreview = [], fyid, listFyear = [], status, FromFYObj, ToFYObj }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    setOpenModal(false);
  };

  const handleAction = async (action) => {
    setLoading(true);
    try {
      if (action === 'transfer') {
        const response = await CreateFinancialYearAndTransferData({
          aliasname: '',
          companybranchid: 0,
          comments: '',
          startyear: String(ToFYObj.startyear),
          startdate: ToFYObj.startdate,
          enddate: ToFYObj.enddate,
          endyear: String(ToFYObj.endyear),
          excludedaccountidlist: '',
          excludedaccountgroupidlist: '',
          ledger_transfer_option: 3,
          party_transfer_option: 3,
          item_transfer_option: 3,
          transfer_groups_ledgers: 1,
          ledger_closing_balance: 1,
          party_closing_balance:1,
          from_financialyearid: FromFYObj.financialyearid,
          to_financialyearid: ToFYObj.financialyearid,
        });
        if(response?.statuscode !== 'Success') throw new Error(response?.statusmessage || 'Transfer failed');
        showSuccessSnackbar('Balances and Missing Accounts Retransferred Successfully.');
      } else if (action === 'freeze') {
        await API.graphql(graphqlOperation(freezeFinancialYear, { financialyearid: fyid }));
        showSuccessSnackbar('Financial year frozen.');
      } else if (action === 'unfreeze') {
        await API.graphql(graphqlOperation(unFreezeFinancialYear, { financialyearid: fyid }));
        showSuccessSnackbar('Financial year unfrozen.');
      }
      setOpenModal(false);
    } catch (err) {
      showErrorSnackbar(err.message || 'An error occurred.');
    } finally {
      dispatch(fetchUserPreference());
      setLoading(false);
    }
  };

  const hasNextFY = Boolean(ToFYObj?.financialyearid);
  const isPrepareNewFy = item === 'transfer' ? hasNextFY : true;
  const fromRange = formatRange(FromFYObj);
  const toRange = formatRange(ToFYObj);
  
  // For freezing, show only the year being frozen
  const freezingYearRange = FromFYObj ? `${FromFYObj.startyear}-${FromFYObj.endyear}` : '';

  return (
    <Modal
      open={open}
      onClose={handleClose}
      closeAfterTransition
      BackdropProps={{ timeout: 500 }}
      aria-labelledby="year-end-modal-title"
      aria-describedby="year-end-modal-description"
    >
      <Fade in={open}>
        <Paper sx={modalStyle}>
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              bgcolor: 'primary.main',
              px: 2,
              py: 1.5,
            }}
          >
            <Typography id="year-end-modal-title" variant="h6" component="h2" sx={{ color: '#fff', fontWeight: 'bold' }}>
              {item === 'transfer' ? 'Transfer Closing Balance' : 'Close & Freeze Financial Year'}
            </Typography>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon sx={{ color: '#fff' }} />
            </IconButton>
          </Box>

          {/* Content */}
          <Box
            sx={{
              p: { xs: 2, sm: 3 },
              maxHeight: 'calc(80vh - 64px)',
              overflowY: 'auto',
            }}
          >
            {isPrepareNewFy ? (
              <>
                <Typography
                  variant="body2"
                  align="center"
                  sx={{
                    color: '#000000',
                    mb: 2,
                    fontWeight: 'bold',
                    fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                    padding: { xs: '6px', sm: '8px' },
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    borderRadius: '4px',
                    wordBreak: 'break-word',
                    display: 'flex',
                    flexDirection: { xs: 'column', lg: 'row' },
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                  }}
                >
                  {item === 'transfer' ? `${fromRange} to ${toRange}` : `Financial Year ${freezingYearRange}`}
                </Typography>
                <Box
                  sx={{
                    border: '1px dashed grey',
                    borderRadius: 2,
                    bgcolor: '#f9f9f9',
                    p: 3,
                    mb: 4,
                    boxShadow: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ lineHeight: 1.8, textAlign: 'justify', fontSize: '14px', fontFamily: 'inherit' }}>
                    <Typography component="span" variant="body2" sx={{ fontWeight: 600, fontSize: '14px', fontFamily: 'inherit' }}>
                      Note:&nbsp;
                    </Typography>

                    {item === 'transfer' ? (
                      <>
                        {transferContent}
                        <Box sx={{ mt: 'auto' }}>
                          <Typography variant="body2" sx={{ mt: 2, fontWeight: 'bold', color: 'red' }}>
                            Last Transferred Date : {FromFYObj?.transferdate ? formatUnixTimestamp(FromFYObj?.transferdate) : '-'}
                          </Typography>
                        </Box>{' '}
                      </>
                    ) : (
                      <>
                        Freezing the financial year will lock all transactions for the period{' '}
                        <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
                          {freezingYearRange}
                        </Typography>
                        . No further entries or edits will be allowed once it is frozen. Please complete any pending adjustments before
                        confirming.
                      </>
                    )}
                  </Typography>
                </Box>
              </>
            ) : (
              <Stack
                direction="column"
                spacing={3}
                alignItems="center"
                justifyContent="center"
                sx={{
                  mb: 4,
                  p: 3,
                  backgroundColor: '#fff8f8',
                  borderRadius: 2,
                  border: '1px solid #ffcdd2',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                  maxHeight: 'none',
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    color: 'error.main',
                    fontWeight: 500,
                    fontSize: {
                      xs: '0.8rem',
                      sm: '0.9rem',
                      md: '1rem',
                    },
                    textAlign: 'center',
                  }}
                >
                  Next Financial Year is not created, so the balance cannot be carried forward.
                </Typography>
                <Box
                  sx={{
                    width: {
                      xs: '100%',
                      sm: '50%',
                      md: '30%',
                    },
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/create-new-financial-year')}
                    fullWidth
                    sx={{
                      px: 3,
                      py: 1,
                      borderRadius: 2,
                      borderWidth: 2,
                      '&:hover': {
                        borderWidth: 2,
                        backgroundColor: 'error.light',
                        color: 'error.main',
                      },
                    }}
                  >
                    Create New
                  </Button>
                </Box>
              </Stack>
            )}

            {/* Actions */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <LoadingButton disabled={loading} variant="outlined" onClick={handleClose} fullWidth>
                {item === 'transfer' ? 'No' : 'Close'}
              </LoadingButton>

              {item === 'transfer' ? (
                <LoadingButton
                  fullWidth
                  variant="contained"
                  onClick={() => handleAction('transfer')}
                  loading={loading}
                  disabled={!isPrepareNewFy}
                >
                  Yes (Sync Now)
                </LoadingButton>
              ) : (
                <>
                  <LoadingButton
                    fullWidth
                    variant="contained"
                    onClick={() => handleAction(status === 1 ? 'unfreeze' : 'freeze')}
                    loading={loading}
                    disabled={!isPrepareNewFy}
                  >
                    {status === 1 ? 'Confirm Unfreeze' : 'Confirm Freeze'}
                  </LoadingButton>
                </>
              )}
            </Stack>
          </Box>
        </Paper>
      </Fade>
    </Modal>
  );
}
